import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { CampaignUrlBuilder } from './url-builder';
import { filter, map, take } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CampaignCrudUrlBuilder } from './crud-url-builder';
import { ISelectOption } from '../../../shared/form/element/base-select/select-option.interface';
import { CampaignId } from './campaign-id';

@Injectable({
  providedIn: 'root'
})
export class PhotographerOptionsService {

  loading: boolean = false;
  photographers: any;
  photographersOptions: ISelectOption[];
  photographerUrl: string;
  sub: ReplaySubject<any> = new ReplaySubject<any>(1);
  photographersSubject: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  routeChangeSubscription: Subscription;

  constructor(private httpClient: HttpClient,
              private router: Router,
              private route: ActivatedRoute,
              private urlBuilder: CampaignCrudUrlBuilder,
              private campaignId: CampaignId) {
    this.init();
  }

  init() {
    this.routeChangeSubscription = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route)
    )
      .subscribe((event: any) => {
        const url = this.urlBuilder.buildUrl('/photographer');
        if (url !== this.photographerUrl) {
          this.photographerUrl = url;
          this.photographers = null;
          this.photographersOptions = null;
          this.loading = false;
          this.sub = new ReplaySubject<any>(1);
          this.photographersSubject = new ReplaySubject<any[]>(1);
        }
      });
  }

  load(cache = true) {
    if (!this.campaignId.getId()) {
      this.campaignId.channel.pipe(filter(id => !!id), take(1)).subscribe(() => {
        this.loading = false;
        this.load(cache);
      });
      return;
    }
    const url = this.urlBuilder.buildUrl('/photographer');
    if (this.photographerUrl === url && this.photographers && cache) {
      return;
    }
    this.photographerUrl = url;
    this.httpClient.get(this.photographerUrl, { params: { limit: '9999' } })
      .subscribe((response: any) => {
        this.photographers = response.rows;
        const parentIds = new Set(response.rows.filter(r => r.parentPhotographer).map(r => r.parentPhotographer.id));
        this.photographersOptions = response.rows.map((row) => {
          const suffix = row.parentPhotographer ? ' (sub)' : parentIds.has(row.id) ? ' (parent)' : '';
          return <ISelectOption>{ value: row.id, label: `${row.firstName} ${row.lastName}${suffix}` };
        });
        this.photographersSubject.next(this.photographers);
        this.sub.next(this.photographersOptions);
        this.loading = false;
      }, (e) => {
        this.sub.error(e);
        this.photographersSubject.error(e);
        this.loading = false;
      });
  }

  getPhotographers(cache: boolean = true): Observable<any[]> {
    if (this.photographers && cache) {
      return of(this.photographers);
    }
    if (!this.loading) {
      this.load(cache);
      this.loading = true;
    }
    return this.photographersSubject.pipe(take(1));
  }

  getPhotographersOptionsByPhotoType(photoType: string, cache: boolean = true): Observable<ISelectOption[]> {
    const mapToOptions = (rows: any[]) => rows
      .filter(p => p.photoType === photoType)
      .map(p => <ISelectOption>{ value: p.id, label: `${p.firstName} ${p.lastName}` });

    if (this.photographers && cache) {
      return of(mapToOptions(this.photographers));
    }
    if (!this.loading) {
      this.load(cache);
      this.loading = true;
    }
    return this.photographersSubject.pipe(take(1), map(mapToOptions));
  }

  getPhotographersOptions(cache: boolean = true): Observable<ISelectOption[]> {
    if (!this.photographersOptions || !cache) {
      if (!this.loading) {
        this.load(cache);
        this.loading = true;
      }
      return this.sub;
    } else {
      return of(this.photographersOptions);
    }
  }
}
